import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { Component, effect, signal, TemplateRef, untracked, ViewChild, ViewContainerRef, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { differenceInHours, differenceInMinutes } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { FuseAlertComponent } from '../../../../@fuse/components/alert/alert.component';
import { FuseDrawerComponent } from '../../../../@fuse/components/drawer/drawer.component';
import { LocalizeDatePipe } from '../../../core/pipes/localizeDate.pipe';
import { LocalizePipe } from '../../../core/pipes/standalone/localize.pipe';
import { WebSocketGenericMessage } from '../../../core/websocket/websocket.types';
import { FileService } from '../../../core/file/file.service';
import { WebsocketService } from '../../../core/websocket/websocket.service';

@Component({
    selector: 'ws-messages',
    templateUrl: './ws-messages.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        RouterModule,
        MatIconModule,
        FuseAlertComponent,
        FuseDrawerComponent,
        MatBadgeModule,
        MatButtonModule,
        MatTooltipModule,
        LocalizeDatePipe,
        LocalizePipe,
        TranslocoModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class WsMessagesComponent {

    @ViewChild('newMessagePanel') private _newMessagePanel: TemplateRef<any>;
    @ViewChild('messagesOrigin') private _messagesOrigin: MatButton;

    messages = signal<WebSocketGenericMessage[]>([]);
    newMessage = signal<WebSocketGenericMessage>(undefined);
    shakeState = false;

    private _overlayRef: OverlayRef;

    constructor(
        private fileService: FileService,
        private _overlay: Overlay,
        private transloco: TranslocoService,
        private _viewContainerRef: ViewContainerRef,
        private websocketService: WebsocketService
    ) {
        effect(() => {
            const genericMessages = this.websocketService.genericMessages();
            untracked(() => this.messages.set(genericMessages.filter(message => Boolean(message.payload._genericAlertConfig_))));
        });

        effect(() => {
            const newMessage = this.websocketService.newGenericMessage();
            untracked(() => this.newMessage.set(newMessage));
            if (newMessage) {
                this.shakeState = true;
                setTimeout(() => this.shakeState = false, 1000);
                this.openPanel();
            }
        })
    }

    dismissAlert(index: number) {
        this.messages.update(messages => {
            messages.splice(index, 1);
            return [...messages];
        });
        this.websocketService.setGenericMessages(this.messages());
    }

    dismissAll() {
        this.websocketService.setGenericMessages([]);
    }

    dismissNewAlert() {
        this.newMessage = undefined;
        this.closePanel();
    }

    async downloadFile(path: string) {
        const split = path.split('/');
        const filename = split[split.length - 1];
        const tempFile = await firstValueFrom(this.fileService.download(path));
        if (tempFile) {
            const e = document.createElement('a');
            e.href = tempFile;
            e.target = e.target;
            e.download = filename;
            e.click();
        }
    }

    getDifference(message: WebSocketGenericMessage) {
        const today = new Date();
        const receivedDate = new Date(message.receivedDate);
        const diffInHours = differenceInHours(today, receivedDate);
        if (diffInHours === 0) {
            const diffInMinutes = differenceInMinutes(today, receivedDate);
            return this.transloco.translate('diffInMinutes', { minutes: diffInMinutes });
        } else if (diffInHours < 24) {
            const diffInMinutes = differenceInMinutes(today, receivedDate);
            const minutes = diffInMinutes % 60;
            if (minutes === 0) {
                return this.transloco.translate('diffInHours', { hours: diffInHours });
            } else if (minutes > 0) {
                return this.transloco.translate('diffInHoursAndMinutes', { hours: diffInHours, minutes });
            }
        } else if (diffInHours >= 24) {
            return this.transloco.translate('diffMoreThan1Day');
        }
        return diffInHours;
    }

    getBadgeText(): number | string {
        return this.messages.length > 9 ? '9+' : this.messages.length;
    }

    openPanel(): void {
        // Return if the messages panel or its origin is not defined
        if (!this._newMessagePanel || !this._messagesOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        if (!this._overlayRef.hasAttached()) {
            this._overlayRef.attach(new TemplatePortal(this._newMessagePanel, this._viewContainerRef));
        }
    }

    closePanel(): void {
        this._overlayRef.detach();
    }

    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._messagesOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
